import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Clock, AlertCircle, CheckCircle2, X, RefreshCw, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const AttendanceNotifications = () => {
  const { api, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAcknowledgeDialog, setShowAcknowledgeDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [acknowledgeNote, setAcknowledgeNote] = useState('');
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, summaryRes] = await Promise.all([
        api.get('/attendance/notifications?limit=20'),
        api.get('/attendance/notifications/summary')
      ]);
      setNotifications(notifRes.data.notifications || []);
      setSummary(summaryRes.data);
    } catch (err) {
      // User might not have permission - that's okay
      if (err.response?.status !== 403) {
        console.error('Error fetching notifications:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/attendance/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/attendance/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const acknowledgeNotification = async () => {
    if (!selectedNotification) return;
    try {
      await api.post(`/attendance/notifications/${selectedNotification.id}/acknowledge`, {
        note: acknowledgeNote
      });
      toast.success('Notification acknowledged');
      setShowAcknowledgeDialog(false);
      setSelectedNotification(null);
      setAcknowledgeNote('');
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to acknowledge notification');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'late_arrival':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'absent':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Bell className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Don't render if user doesn't have permission
  if (!summary?.authorized) {
    return null;
  }

  const unreadCount = summary?.unread_count || 0;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="attendance-notifications-btn"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-3 border-b bg-slate-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1e3a5f]">Attendance Alerts</h3>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => fetchNotifications()}
                  className="h-8 w-8 p-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={markAllAsRead}
                    className="text-xs"
                  >
                    Mark all read
                  </Button>
                )}
              </div>
            </div>
            {/* Summary badges */}
            <div className="flex gap-2 mt-2">
              {summary?.today_late > 0 && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <Clock className="w-3 h-3 mr-1" />
                  {summary.today_late} late today
                </Badge>
              )}
              {summary?.today_absent > 0 && (
                <Badge variant="outline" className="text-red-600 border-red-300">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {summary.today_absent} absent
                </Badge>
              )}
            </div>
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p className="font-medium">All clear!</p>
                <p className="text-sm">No attendance issues to report</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notif) => {
                  const isRead = notif.read_by?.includes(user?.id);
                  return (
                    <div 
                      key={notif.id}
                      className={`p-3 hover:bg-slate-50 cursor-pointer transition-colors ${!isRead ? 'bg-blue-50/50' : ''}`}
                      onClick={() => markAsRead(notif.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!isRead ? 'font-medium' : ''}`}>
                            {notif.message}
                          </p>
                          {notif.details && (
                            <div className="mt-1 text-xs text-slate-500">
                              {notif.type === 'late_arrival' && (
                                <span>Expected: {notif.details.expected_time} | Arrived: {notif.details.actual_time}</span>
                              )}
                              {notif.type === 'absent' && notif.details.role && (
                                <span className="capitalize">{notif.details.role.replace(/_/g, ' ')}</span>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-slate-400">
                              {formatDate(notif.created_at)} at {formatTime(notif.created_at)}
                            </span>
                            {!notif.acknowledged && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-blue-600 hover:text-blue-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedNotification(notif);
                                  setShowAcknowledgeDialog(true);
                                }}
                              >
                                Acknowledge
                              </Button>
                            )}
                            {notif.acknowledged && (
                              <Badge variant="outline" className="text-xs text-green-600">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Acknowledged
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="p-2 border-t bg-slate-50">
            <Button 
              variant="ghost" 
              className="w-full justify-between text-sm"
              onClick={() => {
                setOpen(false);
                window.location.href = '/attendance';
              }}
            >
              View all attendance
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Acknowledge Dialog */}
      <Dialog open={showAcknowledgeDialog} onOpenChange={setShowAcknowledgeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acknowledge Notification</DialogTitle>
            <DialogDescription>
              Mark this attendance issue as acknowledged. You can add a note about any follow-up action taken.
            </DialogDescription>
          </DialogHeader>
          
          {selectedNotification && (
            <div className="py-4">
              <div className="bg-slate-50 p-3 rounded-lg mb-4">
                <div className="flex items-center gap-2 mb-2">
                  {getNotificationIcon(selectedNotification.type)}
                  <span className="font-medium">{selectedNotification.staff_name}</span>
                </div>
                <p className="text-sm text-slate-600">{selectedNotification.message}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Follow-up Note (optional)</label>
                <Textarea
                  placeholder="e.g., Spoke with employee, was due to transport issues..."
                  value={acknowledgeNote}
                  onChange={(e) => setAcknowledgeNote(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcknowledgeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={acknowledgeNotification}>
              Acknowledge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttendanceNotifications;
